// src/sections/Admin/AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiX, 
  FiUpload, 
  FiSave,
  FiArrowLeft,
  FiImage,
  FiLogOut,
  FiCheckCircle,
  FiUser,
  FiAlertCircle,
  FiRefreshCw
} from 'react-icons/fi';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [newProject, setNewProject] = useState({
    title_ar: '',
    title_en: '',
    images: []
  });
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // التحقق من صحة تسجيل الدخول
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const userData = localStorage.getItem('adminUser');
    
    if (!token) {
      navigate('/admin/login');
    } else {
      if (userData) {
        setUser(JSON.parse(userData));
      }
      fetchProjects();
    }
  }, [navigate]);

  // دالة لجلب جميع المشاريع من API
  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('https://taamirnajd.com/api/projects', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data.data || data); // حسب هيكل الاستجابة
      } else {
        console.error('Failed to fetch projects');
        showError('فشل في تحميل المشاريع');
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      showError('حدث خطأ أثناء تحميل المشاريع');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      // إرسال طلب logout إلى API
      await fetch('https://taamirnajd.com/api/auth/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        }
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // مسح البيانات المحلية
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      localStorage.removeItem('adminAuthenticated');
      navigate('/');
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showError = (message) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 3000);
  };

  const handleInputChange = (e, field) => {
    const value = e.target.value;
    setNewProject(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditInputChange = (e, field) => {
    const value = e.target.value;
    setEditingProject(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);

    // إنشاء معاينات للصور وتخزين ملفاتها
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      isNew: true
    }));
    
    setNewProject(prev => ({
      ...prev,
      images: [...prev.images, ...newImages]
    }));
    
    setUploading(false);
    showSuccess('تم تحميل الصور بنجاح');
  };

  const addProject = async () => {
    if (!newProject.title_ar || !newProject.title_en) {
      showError('يرجى ملء جميع الحقول');
      return;
    }

    if (newProject.images.length === 0) {
      showError('يرجى إضافة صورة واحدة على الأقل');
      return;
    }

    setProcessing(true);

    try {
      const token = localStorage.getItem('adminToken');
      
      // استخدام FormData لإرسال النص والصور معاً
      const formData = new FormData();
      formData.append('title_ar', newProject.title_ar);
      formData.append('title_en', newProject.title_en);
      
      // إضافة جميع الصور إلى FormData
      newProject.images.forEach((image, index) => {
        formData.append(`images[${index}]`, image.file);
      });

      const projectResponse = await fetch('https://taamirnajd.com/api/projects', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (projectResponse.ok) {
        showSuccess('تم إضافة المشروع بنجاح');
        setNewProject({
          title_ar: '',
          title_en: '',
          images: []
        });
        fetchProjects(); // إعادة تحميل المشاريع
      } else {
        const errorData = await projectResponse.json();
        showError(errorData.message || 'فشل في إضافة المشروع');
      }
    } catch (error) {
      console.error('Error adding project:', error);
      showError('حدث خطأ أثناء إضافة المشروع');
    } finally {
      setProcessing(false);
    }
  };

  // دالة لتحميل الصور الجديدة فقط
  const uploadNewImages = async (projectId, images) => {
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    
    // إضافة الصور الجديدة فقط إلى FormData
    images.forEach((image, index) => {
      formData.append(`images[${index}]`, image.file);
    });

    try {
      const response = await fetch(`https://taamirnajd.com/api/projects/${projectId}/images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('فشل في تحميل الصور الجديدة');
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading new images:', error);
      throw error;
    }
  };

  const updateProject = async () => {
    if (!editingProject) return;

    if (!editingProject.title_ar || !editingProject.title_en) {
      showError('يرجى إدخال عنوان المشروع باللغتين');
      return;
    }

    setProcessing(true);

    try {
      const token = localStorage.getItem('adminToken');
      
      // تحديث بيانات المشروع أولاً
      const formData = new FormData();
      formData.append('title_ar', editingProject.title_ar);
      formData.append('title_en', editingProject.title_en);
      formData.append('_method', 'PUT');

      const response = await fetch(`https://taamirnajd.com/api/projects/${editingProject.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'فشل في تحديث المشروع');
      }

      // إذا كان هناك صور جديدة، قم بتحميلها
      const newImages = editingProject.images.filter(img => img.isNew);
      if (newImages.length > 0) {
        await uploadNewImages(editingProject.id, newImages);
      }

      showSuccess('تم تحديث المشروع بنجاح');
      setEditingProject(null);
      fetchProjects(); // إعادة تحميل المشاريع
    } catch (error) {
      console.error('Error updating project:', error);
      showError(error.message || 'حدث خطأ أثناء تحديث المشروع');
    } finally {
      setProcessing(false);
    }
  };

  const removeImage = async (imageId, projectId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`https://taamirnajd.com/api/projects/${projectId}/images/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        }
      });

      if (response.ok) {
        showSuccess('تم حذف الصورة بنجاح');
        // تحديث حالة التعديل محلياً دون الحاجة لإعادة تحميل كل المشاريع
        setEditingProject(prev => ({
          ...prev,
          images: prev.images.filter(img => img.id !== imageId)
        }));
      } else {
        showError('فشل في حذف الصورة');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      showError('حدث خطأ أثناء حذف الصورة');
    }
  };

  const deleteProject = async (id, silent = false) => {
    if (!silent && !window.confirm('هل أنت متأكد من حذف هذا المشروع؟')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`https://taamirnajd.com/api/projects/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        }
      });

      if (response.ok) {
        if (!silent) showSuccess('تم حذف المشروع بنجاح');
        fetchProjects(); // إعادة تحميل المشاريع
      } else {
        if (!silent) showError('فشل في حذف المشروع');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      if (!silent) showError('حدث خطأ أثناء حذف المشروع');
    }
  };

  const startEditing = (project) => {
    const formattedProject = {
      ...project,
      images: project.images ? project.images.map(img => ({
        id: img.id,
        url: img.url,
        preview: img.url,
        isNew: false
      })) : []
    };
    setEditingProject(formattedProject);
  };

  const cancelEditing = () => {
    setEditingProject(null);
  };

  const removeTempImage = (index) => {
    if (newProject.images[index]?.preview) {
      URL.revokeObjectURL(newProject.images[index].preview);
    }
    
    setNewProject(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const removeEditImage = async (index, imageId, projectId) => {
    if (imageId) {
      await removeImage(imageId, projectId);
    } else {
      // إزالة الصور الجديدة (التي لم يتم رفعها بعد) من الواجهة فقط
      if (editingProject.images[index]?.preview) {
        URL.revokeObjectURL(editingProject.images[index].preview);
      }
      
      setEditingProject(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    }
  };

  const handleEditImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);

    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      isNew: true // وضع علامة أن هذه الصور جديدة
    }));
    
    setEditingProject(prev => ({
      ...prev,
      images: [...prev.images, ...newImages]
    }));
    
    setUploading(false);
    showSuccess('تم تحميل الصور بنجاح');
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>جاري تحميل البيانات...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="user-info">
          {user && (
            <>
              <FiUser />
              <span>مرحباً، {user.name}</span>
            </>
          )}
        </div>
        <div className="header-actions">
          <button onClick={handleLogout} className="logout-btn">
            <FiLogOut />
            تسجيل الخروج
          </button>
        </div>
      </div>
      
      {successMessage && (
        <div className="success-toast">
          <FiCheckCircle />
          <span>{successMessage}</span>
        </div>
      )}
      
      {errorMessage && (
        <div className="error-toast">
          <FiAlertCircle />
          <span>{errorMessage}</span>
        </div>
      )}
      
      <h1>{t('admin.dashboardTitle')}</h1>
      
      <div className="admin-content">
        {/* نموذج إضافة/تعديل مشروع */}
        <div className="project-form glass-effect">
          <h2>
            {editingProject ? (
              <>
                <FiEdit style={{ marginLeft: '10px' }} />
                {t('admin.editProject')}
              </>
            ) : (
              <>
                <FiPlus style={{ marginLeft: '10px' }} />
                {t('admin.addProject')}
              </>
            )}
          </h2>
          
          <div className="form-group">
            <label>{t('admin.projectTitleAr')} *</label>
            <input
              type="text"
              value={editingProject ? editingProject.title_ar : newProject.title_ar}
              onChange={editingProject ? 
                (e) => handleEditInputChange(e, 'title_ar') : 
                (e) => handleInputChange(e, 'title_ar')
              }
              placeholder="أدخل العنوان بالعربية"
            />
          </div>
          
          <div className="form-group">
            <label>{t('admin.projectTitleEn')} *</label>
            <input
              type="text"
              value={editingProject ? editingProject.title_en : newProject.title_en}
              onChange={editingProject ? 
                (e) => handleEditInputChange(e, 'title_en') : 
                (e) => handleInputChange(e, 'title_en')
              }
              placeholder="Enter title in English"
            />
          </div>
          
          <div className="form-group">
            <label>
              <FiImage style={{ marginLeft: '5px' }} />
              {t('admin.projectImages')} *
            </label>
            
            {/* إخفاء زر "اختر الصور" في وضع التعديل */}
            {!editingProject && (
              <div className="file-upload-wrapper">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="file-upload-input"
                  id="project-images"
                />
                <label htmlFor="project-images" className="file-upload-label">
                  <FiUpload />
                  <span>{uploading ? 'جاري الرفع...' : 'اختر الصور'}</span>
                </label>
              </div>
            )}
            
            {uploading && (
              <div className="upload-status">
                <div className="loading-spinner"></div>
                <span>{t('admin.uploading')}</span>
              </div>
            )}
            
            <div className="image-previews">
              {/* إضافة زر Plus في حالة التعديل */}
              {editingProject && (
                <div className="image-preview add-more-container">
                  <label htmlFor="add-more-images" className="add-more-btn">
                    <FiPlus size={24} />
                    <span>إضافة صور</span>
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleEditImageUpload}
                    id="add-more-images"
                    className="hidden-input"
                  />
                </div>
              )}
              
              {(editingProject ? editingProject.images : newProject.images).map((image, index) => (
                <div key={index} className="image-preview">
                  <img src={image.preview} alt={`Preview ${index}`} />
                  <button 
                    type="button" 
                    onClick={() => editingProject ? 
                      removeEditImage(index, image.id, editingProject.id) : 
                      removeTempImage(index)
                    }
                    className="remove-image-btn"
                  >
                    <FiX />
                  </button>
                  {image.isNew && <span className="new-badge">جديد</span>}
                </div>
              ))}
            </div>
          </div>
          
          <div className="form-actions">
            {editingProject ? (
              <>
                <button 
                  type="button" 
                  onClick={updateProject} 
                  className="btn-primary"
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <div className="button-spinner"></div>
                      جاري الحفظ...
                    </>
                  ) : (
                    <>
                      <FiSave />
                      {t('admin.updateProject')}
                    </>
                  )}
                </button>
                <button 
                  type="button" 
                  onClick={cancelEditing} 
                  className="btn-secondary"
                  disabled={processing}
                >
                  <FiArrowLeft />
                  {t('admin.cancel')}
                </button>
              </>
            ) : (
              <button 
                type="button" 
                onClick={addProject} 
                className="btn-primary" 
                disabled={processing || !newProject.title_ar || !newProject.title_en || newProject.images.length === 0}
              >
                {processing ? (
                  <>
                    <div className="button-spinner"></div>
                    جاري الإضافة...
                  </>
                ) : (
                  <>
                    <FiPlus />
                    {t('admin.addProject')}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        
        {/* قائمة المشاريع الحالية */}
        <div className="projects-list glass-effect">
          <h2>
            <FiImage style={{ marginLeft: '10px' }} />
            {t('admin.existingProjects')} ({projects.length})
          </h2>
          
          {projects.length === 0 ? (
            <div className="empty-state">
              <FiImage size={48} color="#6c757d" />
              <p>{t('admin.noProjects')}</p>
              <small>ابدأ بإضافة مشروعك الأول باستخدام النموذج على اليسار</small>
            </div>
          ) : (
            <div className="projects-grid">
              {projects.map(project => (
                <div key={project.id} className="project-item shadow-luxury">
                  <div className="project-image">
                    <img src={project.images?.[0]?.url || '/placeholder-image.jpg'} alt={project.title_ar} />
                    <span className="image-count">
                      {project.images?.length || 0} صورة
                    </span>
                  </div>
                  <div className="project-details">
                    <h3 className="text-gradient">{project.title_ar}</h3>
                    <p>{project.title_en}</p>
                    <div className="project-actions">
                      <button 
                        onClick={() => startEditing(project)}
                        className="btn-edit"
                      >
                        <FiEdit />
                        {t('admin.edit')}
                      </button>
                      <button 
                        onClick={() => deleteProject(project.id)}
                        className="btn-delete"
                      >
                        <FiTrash2 />
                        {t('admin.delete')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;